/**
 * ThemeProvider — applique les couleurs choisies dans l'admin (section « Thème »)
 * en surchargeant les variables CSS du site (:root). Non destructif : si une
 * couleur n'est pas définie dans le CMS, la valeur par défaut d'index.css reste.
 *
 * Les variables sont au format HSL "H S% L%" (consommées via hsl(var(--x))),
 * donc on convertit chaque couleur hex saisie en triplet HSL.
 */
import { useSiteContent } from "@/lib/useSiteContent";
import { keysFor } from "@/config/cmsSections";

// hex (#RRGGBB) → "H S% L%" ; null si invalide
function hexToHslTriple(hex) {
    if (typeof hex !== "string") return null;
    const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
    if (!m) return null;
    const int = parseInt(m[1], 16);
    const r = ((int >> 16) & 255) / 255;
    const g = ((int >> 8) & 255) / 255;
    const b = (int & 255) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
        else if (max === g) h = (b - r) / d + 2;
        else h = (r - g) / d + 4;
        h /= 6;
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// clé CMS → variable(s) CSS à surcharger
const MAP = {
    "theme.primary":          ["--primary", "--ring"],
    "theme.primary_fg":       ["--primary-foreground"],
    "theme.accent":           ["--accent"],
    "theme.accent_fg":        ["--accent-foreground"],
    "theme.background":       ["--background"],
    "theme.foreground":       ["--foreground"],
    "theme.secondary":        ["--secondary"],
    "theme.muted_foreground": ["--muted-foreground"],
};

export default function ThemeProvider() {
    const c = useSiteContent(keysFor("theme"));
    const lines = [];
    for (const [key, vars] of Object.entries(MAP)) {
        const hex = c(key, "");            // "" si non défini → on ignore
        const hsl = hexToHslTriple(hex);
        if (hsl) vars.forEach((v) => lines.push(`${v}: ${hsl};`));
    }
    if (lines.length === 0) return null;
    return <style dangerouslySetInnerHTML={{ __html: `:root{${lines.join("")}}` }} />;
}
